<template>
  <div class="mb-8 ml-4 mr-4 md:mb-8 md:ml-[5%] md:mr-[5%] e1:mx-auto e1:w-[90%] e1:max-w-[1136px]">
    <!-- 見出し部分 -->
    <div class="mb-2 mt-8 md:mt-12">
      <h3 class="mb-1 flex min-w-0 scroll-mt-20 font-Spectral text-xl tracking-wide text-gold4 md:text-[26px] md:leading-[40px]">
        {{ title }}
        <span class="ml-2 mr-1 flex flex-grow items-center md:mx-2">
          <span class="h-[0.5px] flex-grow bg-gold4"></span>
        </span>
      </h3>
    </div>

    <!-- 体験件数表示とフィルターボタン（Desktop は右端に View on Map／Mobile は右端に list/map トグル） -->
    <div class="mb-4">
      <!-- 件数と並び順は同じ行。並び順は右端 -->
      <div class="mb-2 flex items-center justify-between gap-4 md:mb-4 md:pl-1 xl:pl-1.5">
        <p class="text-left font-['Spectral'] text-base font-light leading-tight text-stone-500">{{ displayCount }} experiences found</p>
        <ExperienceSortSelect :model-value="selectedSortType" :on-change="onSortChange" />
      </div>
      <div class="flex items-center gap-4">
        <div class="min-w-0 flex-1">
          <FilterControlSection
            :destination-tags-query="destinationTagsQuery"
            :experience-event-start-at-dates-query="experienceEventStartAtDatesQuery"
            :on-instant-booking-click="onInstantBookingClick"
            :preference-tags-query="preferenceTagsQuery"
            :selected-dates="[]"
            :theme-tags-query="themeTagsQuery"
            single-row-scroll
          />
        </div>
        <!-- Mobile: list/map トグル（検索バーと同一行・右。Figma 準拠）。座標付き体験があるときのみ表示 -->
        <div
          v-if="experiences.length > 0 && !hasNoGeocodedExperiences"
          aria-label="View mode"
          class="flex shrink-0 items-center gap-0.5 rounded border border-stone-300 bg-white p-0.5 md:hidden"
          role="tablist"
        >
          <button
            aria-label="List view"
            role="tab"
            type="button"
            :aria-selected="viewMode === 'list'"
            :class="['flex h-9 w-9 items-center justify-center rounded transition-colors', viewMode === 'list' ? 'bg-gold3 text-white' : 'text-gray4']"
            @click="switchViewMode('list')"
          >
            <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <rect height="6" rx="1" width="16" x="4" y="5" />
              <rect height="6" rx="1" width="16" x="4" y="13" />
            </svg>
          </button>
          <button
            aria-label="Map view"
            role="tab"
            type="button"
            :aria-selected="viewMode === 'map'"
            :class="['flex h-9 w-9 items-center justify-center rounded transition-colors', viewMode === 'map' ? 'bg-gold3 text-white' : 'text-gray4']"
            @click="switchViewMode('map')"
          >
            <svg aria-hidden="true" class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
            </svg>
          </button>
        </div>
        <!-- Desktop: View on Map（地図非表示時のみ。スプリット時は地図側の × で閉じる） -->
        <button
          v-if="isDesktop && !mapOpen && !hasNoGeocodedExperiences"
          class="hidden h-[40px] shrink-0 items-center whitespace-nowrap rounded-sm bg-gold3 px-4 font-Spectral text-[17px] leading-6 text-white shadow-[0px_4px_48px_0px_rgba(0,0,0,0.15)] transition-opacity hover:opacity-90 md:flex"
          type="button"
          @click="openMap"
        >
          <svg aria-hidden="true" class="mr-2 h-[18px] w-[18px]" fill="none" stroke="currentColor" stroke-width="1.25" viewBox="0 0 24 24">
            <path d="M12 21s6-5.686 6-10a6 6 0 1 0-12 0c0 4.314 6 10 6 10Z" stroke-linecap="round" stroke-linejoin="round" />
            <circle cx="12" cy="11" r="2.25" />
          </svg>
          View on Map
        </button>
      </div>
    </div>

    <!-- 標準グリッド（SSR 描画=SEO。地図表示中は非表示）。
         Desktop/Mobile の出し分けは CSS ブレークポイントで行い、isDesktop(JS) 依存の hydration チラつきを避ける。
         Mobile は viewMode='list'（既定）で表示、'map' で非表示。座標付き体験が無いときは常にグリッド。 -->
    <div :class="[viewMode === 'list' || hasNoGeocodedExperiences ? 'block' : 'hidden', !mapOpen || hasNoGeocodedExperiences ? 'md:block' : 'md:hidden']">
      <!-- 体験カードグリッド - 件数に応じた最小高さ設定 -->
      <div :class="gridMinHeightClass">
        <!-- ローディング中のスケルトン -->
        <div v-if="!allDataInitialized || isLoading" class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div v-for="i in skeletonCount" :key="`skeleton-${i}`" class="animate-pulse">
            <div class="h-[280px] rounded-lg bg-gray-200"></div>
          </div>
        </div>

        <!-- 体験カード表示 -->
        <div v-else-if="experiences.length > 0" class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div v-for="(experience, i) in experiences" :key="experience.id" class="h-fit w-full min-w-0">
            <OrganismsPagesEnExperienceCard
              class="h-full w-full overflow-hidden"
              orientation="horizontal"
              theme="light"
              :display-multiple-images="false"
              :experience="experience"
              :on-experience-click="() => onExperienceClick(experience.slug, i + 1)"
            />
          </div>
        </div>
        <!-- 0件の場合の表示 -->
        <div v-else class="flex flex-col items-center justify-center pb-10 text-center md:pb-20">
          <p class="mt-10 text-lg font-normal leading-normal tracking-wide text-stone-700 md:text-xl md:leading-loose">No results found</p>
          <p class="my-4 px-2 text-base font-light leading-normal text-stone-500">Try adjusting your filters to find more experiences.</p>
          <PrimaryButton class="mb-10 w-full max-w-xs" font-mode="en" :is-transparent="true" :on-click="onSearchClick">Search</PrimaryButton>
        </div>
      </div>

      <!-- Show More ボタン -->
      <div v-if="experienceCount > experiencesPerPage" class="mx-auto mt-8 text-center md:w-96">
        <PrimaryButton :on-click="onShowMoreClick"> Explore {{ experienceCount - experiencesPerPage }} More Experiences </PrimaryButton>
      </div>
    </div>

    <!-- スプリット（Desktop）／マップ全画面（Mobile）。表示切替は CSS ブレークポイント（isDesktop 非依存）。
         殻は SSR 描画し、ピン取得完了までスケルトンを見せて「グリッド→スプリット」のチラつきを防ぐ。
         地図本体は Desktop 常設＋Mobile は map トグル時のみマウントし、SSR/未マウント間はスケルトンを表示する。 -->
    <div class="md:gap-8" :class="[viewMode === 'map' && !hasNoGeocodedExperiences ? 'flex' : 'hidden', mapOpen && !hasNoGeocodedExperiences ? 'md:flex' : 'md:hidden']">
      <!-- リストペイン（2カラム。bbox 連動＋クライアントページネーション） -->
      <div ref="mapListPaneRef" class="hidden md:block md:w-[58%]">
        <!-- ピン取得中（SSR〜client 取得完了まで）はスケルトン -->
        <div v-if="pinsLoading" class="grid grid-cols-2 gap-x-4 gap-y-8">
          <div v-for="i in 6" :key="`pin-skeleton-${i}`" class="animate-pulse">
            <div class="mb-2 aspect-[3/2] w-full rounded-sm bg-gray-200"></div>
            <div class="mb-1 h-4 w-3/4 rounded bg-gray-200"></div>
            <div class="h-3 w-1/2 rounded bg-gray-200"></div>
          </div>
        </div>
        <p v-else-if="mapPins.length === 0" class="py-10 text-center font-Spectral text-base text-gray4">No experiences in this area.</p>
        <div v-else class="grid grid-cols-2 gap-x-4 gap-y-8">
          <ExperienceMapPinCard
            v-for="(pin, i) in pagedMapPins"
            :key="pin.id"
            :on-click="() => onExperienceClick(pin.slug, i + 1)"
            :on-mouse-enter="handleCardMouseEnter"
            :on-mouse-leave="handleCardMouseLeave"
            :pin="pin"
            :selected="pin.id === selectedPinId"
          />
        </div>
        <div v-if="!pinsLoading && mapListPageCount > 1" class="mt-10 flex justify-center">
          <MoleculesPagination :on-click="onClickMapListPager" :page-count="mapListPageCount" :selected-page-num="mapListPage" />
        </div>
      </div>
      <!-- マップペイン（Desktop=縦長の固定パネル／Mobile=全画面）。右上の × でグリッドへ戻る（Desktop）。
           地図本体は Desktop 常設、Mobile は map トグル時のみマウント（不要なマップロードを避ける）。 -->
      <div class="relative h-[calc(100dvh-210px)] w-full overflow-hidden rounded-lg border border-[#dad6d0] md:sticky md:top-24 md:h-[calc(100vh-160px)] md:flex-1">
        <ClientOnly>
          <ExperienceMapView
            v-if="isDesktop || viewMode === 'map'"
            :are-pins-loading="pinsLoading"
            :area-marker-layer="areaMarkerLayer"
            :highlighted-pin-id="hoveredPinId"
            :on-area-card-click="handleAreaCardClick"
            :on-area-layer-rendered-change="handleAreaLayerRenderedChange"
            :on-bounds-change="handleMapBoundsChange"
            :on-cluster-click="handleClusterClick"
            :on-preview-cta-click="handlePreviewCtaClick"
            :on-select-area="handleSelectArea"
            :on-select-pin="handleSelectPin"
            :on-viewport-search="handleViewportSearch"
            :on-zoom-change="handleMapZoomChange"
            :pins="mapPins"
            :selected-area-destination="selectedAreaDestination"
            :selected-pin-id="selectedPinId"
          />
          <div v-else class="h-full w-full animate-pulse bg-gray5"></div>
          <template #fallback>
            <div class="h-full w-full animate-pulse bg-gray5"></div>
          </template>
        </ClientOnly>
        <button
          aria-label="Close map"
          class="absolute right-3 top-3 z-20 hidden h-9 w-9 items-center justify-center rounded-sm bg-white text-gray4 shadow-[0px_4px_48px_0px_rgba(0,0,0,0.15)] md:flex"
          type="button"
          @click="closeMap"
        >
          <svg aria-hidden="true" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core';

import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { PrimaryButton } from '@/wabunka-src/common/components';
import ExperienceMapPinCard from '@/wabunka-src/common/features/Experience/components/ExperienceMapView/ExperienceMapPinCard.vue';
import ExperienceMapView from '@/wabunka-src/common/features/Experience/components/ExperienceMapView/ExperienceMapView.vue';
import ExperienceSortSelect from '@/wabunka-src/common/features/Experience/components/ExperienceSortSelect/ExperienceSortSelect.vue';
import FilterControlSection from '@/wabunka-src/common/features/Experience/components/FilterControlSection/FilterControlSection.vue';
import { useExperienceMapView } from '@/wabunka-src/common/features/Experience/composables/useExperienceMapView';
import { toExperienceListApiSort, type ExperienceSortType } from '@/wabunka-src/common/features/Experience/utils/experienceListSort';
import type { MapSourcePage } from '@/wabunka-src/common/utils/googleAnalytics/common/mapSearch';

interface Props {
  title: string;
  experienceCount: number;
  experiences: WabunkaExperienceViewModel[];
  isLoading: boolean;
  allDataInitialized: boolean;
  destinationTagsQuery: string;
  themeTagsQuery: string;
  experienceEventStartAtDatesQuery: string;
  experiencesPerPage: number;
  preferenceTagsQuery?: string;
  selectedSortType: ExperienceSortType;
  /** カードクリック時のコールバック。position は表示中の並びでの順位（1 始まり。マップのリストペインを含む） */
  onExperienceClick: (slug: string, position: number) => void;
  onInstantBookingClick?: () => void;
  onSearchClick: () => void;
  onShowMoreClick: () => void;
  onSortChange: (sortType: ExperienceSortType) => void;
  /** GA map_* イベントの source_page（JCAT-4824。どの LP のマップかの内訳に使う） */
  mapSourcePage: MapSourcePage;
}

const props = withDefaults(defineProps<Props>(), {
  preferenceTagsQuery: '',
  onInstantBookingClick: undefined,
});

const gridMinHeightClass = computed(() => {
  if (!props.allDataInitialized || props.isLoading) {
    return 'min-h-[600px]';
  }
  return '';
});

// ローディング中スケルトンの表示枚数（テンプレート内で関数を呼ばないよう computed に切り出す）
const skeletonCount = computed<number>(() => Math.min(props.experienceCount || props.experiencesPerPage, props.experiencesPerPage));

/**
 * 一覧マップビュー（JCAT-5406）。destination / theme / destination×theme ランディングでも
 * 検索ページと同じ挙動で提供する。
 * - Desktop は既定でスプリット（左=ピン一覧／右=マップ）表示。× でグリッド（3カラム）へ戻る。
 * - Mobile は list/map トグルで全画面マップに切替（既定=list）。
 * - マップ⇔リスト連動の実体は検索ページと共通の useExperienceMapView に集約している。
 */
// Desktop 判定（>=768px = Tailwind md）。SSR/初期は false（=グリッドを SSR 描画し SEO を担保）。
const isDesktop = useMediaQuery('(min-width: 768px)');

// モバイルの list/map トグル状態（既定=list）。Desktop の開閉は mapOpen が担う。
const viewMode = ref<'list' | 'map'>('list');
const switchViewMode = (mode: 'list' | 'map') => {
  viewMode.value = mode;
};

const {
  mapPins,
  mapOpen,
  hoveredPinId,
  selectedPinId,
  mapListPage,
  mapListPageCount,
  mapListPaneRef,
  pagedMapPins,
  pinsLoading,
  hasNoGeocodedExperiences,
  isMapActive,
  openMap,
  closeMap,
  handleMapBoundsChange,
  handleSelectPin,
  handleClusterClick,
  handlePreviewCtaClick,
  handleViewportSearch,
  handleCardMouseEnter,
  handleCardMouseLeave,
  onClickMapListPager,
  // エリアラベル層（広域ズーム / JCAT-5434）
  areaMarkerLayer,
  selectedAreaDestination,
  handleMapZoomChange,
  handleAreaLayerRenderedChange,
  handleSelectArea,
  handleAreaCardClick,
} = await useExperienceMapView(
  {
    experienceEventStartAtDates: computed(() => props.experienceEventStartAtDatesQuery),
    destinations: computed(() => props.destinationTagsQuery),
    themes: computed(() => props.themeTagsQuery),
    preferences: computed(() => props.preferenceTagsQuery),
    sort: computed(() => toExperienceListApiSort(props.selectedSortType)),
  },
  { isDesktop, sourcePage: props.mapSourcePage, viewMode },
);

// マップ表示中は「X experiences found」を静的な総件数ではなく、ビューポート内のピン件数で
// 動的表示する（総件数は地図の絞り込みと不一致になり誤情報になるため: レビュー#3）。
// 取得中は前の総件数を見せてチラつかせない。
const displayCount = computed<number>(() => (isMapActive.value && !pinsLoading.value ? mapPins.value.length : props.experienceCount));
</script>
